import React, { useEffect } from 'react';

import type { Text } from '@zillow/constellation';
import {
  AdornedInput,
  Adornment,
  FormField,
  FormHelp,
  InlineFeedback,
  Input,
  Label,
  Paragraph,
} from '@zillow/constellation';

import type { HTMLElementEvent } from '../../../models/Types';
import type { Section } from '../../../models';
import { TENANT_DEFAULT } from '../../../constants';
import { stopPropagationIfInvalidNumber } from '../../../utils/validatorTypes/PositiveIntegerValidator';
import InlineTooltip from '../../Widgets/InlineTooltip';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function TenantDefault({ section, setSectionValues }: Props): JSX.Element {
  function valueIsValid(value: string): boolean {
    return Number(value) >= TENANT_DEFAULT.BOUNDS.MIN_DAYS;
  }

  useEffect(() => {
    section.setCustomFieldValidator(TENANT_DEFAULT.FIELD_NAME.UNPAID_RENT, valueIsValid);
    section.setCustomFieldValidator(TENANT_DEFAULT.FIELD_NAME.OTHER_VIOLATION, valueIsValid);
  }, []);

  function onUnpaidRentChange(event: HTMLElementEvent<typeof Text>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [TENANT_DEFAULT.FIELD_NAME.UNPAID_RENT]: value,
    });
  }

  function onOtherViolationChange(event: HTMLElementEvent<typeof Text>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [TENANT_DEFAULT.FIELD_NAME.OTHER_VIOLATION]: value,
    });
  }

  const unpaidRentHasError = section.getField(TENANT_DEFAULT.FIELD_NAME.UNPAID_RENT).hasError;
  const otherViolationHasError = section.getField(TENANT_DEFAULT.FIELD_NAME.OTHER_VIOLATION).hasError;

  return (
    <>
      <Paragraph fontType="h6" marginBottom="xs">
        How long is the grace period before the tenant{' '}
        <InlineTooltip
          tooltipBodyText="For a tenant to default on the lease, they must fail to live up to the obligations they agreed to in the
          lease."
        >
          defaults
        </InlineTooltip>{' '}
        on the lease?
      </Paragraph>
      <FormField marginBottom="sm">
        <Label id="unpaid-rent">For unpaid rent or utility/service fee</Label>
        <AdornedInput
          error={unpaidRentHasError}
          inputId="unpaid-rent-fee"
          input={
            <Input
              data-test="unpaid-rent"
              aria-labelledby="unpaid-rent"
              type="number"
              onChange={onUnpaidRentChange}
              onKeyDown={stopPropagationIfInvalidNumber}
              value={section.getFieldValue(TENANT_DEFAULT.FIELD_NAME.UNPAID_RENT)}
            />
          }
          rightAdornment={<Adornment>days after receiving notice</Adornment>}
        />
        <FormHelp>{TENANT_DEFAULT.BOUNDS.MIN_DAYS} days is the minimum allowed and most common</FormHelp>
        {unpaidRentHasError && <FormHelp error>Enter a number higher than {TENANT_DEFAULT.BOUNDS.MIN_DAYS}</FormHelp>}
      </FormField>
      <FormField marginBottom="sm">
        <Label id="other-violation">For any other violation that isn’t fixed</Label>
        <AdornedInput
          error={otherViolationHasError}
          inputId="other-violation"
          input={
            <Input
              data-test="other-violation"
              aria-labelledby="other-violation"
              type="number"
              onChange={onOtherViolationChange}
              onKeyDown={stopPropagationIfInvalidNumber}
              value={section.getFieldValue(TENANT_DEFAULT.FIELD_NAME.OTHER_VIOLATION)}
            />
          }
          rightAdornment={<Adornment>days after receiving notice</Adornment>}
        />
        <FormHelp>{TENANT_DEFAULT.BOUNDS.MIN_DAYS} days is the minimum allowed. 30 days is most common</FormHelp>
        {otherViolationHasError && (
          <FormHelp error>Enter a number higher than {TENANT_DEFAULT.BOUNDS.MIN_DAYS}</FormHelp>
        )}
      </FormField>
      <InlineFeedback>
        Under the lease terms, if a tenant is in default, you must notify them before exercising any other rights under
        the lease.
      </InlineFeedback>
    </>
  );
}
